// SDK errors retain authenticated Request/Response objects and sometimes embed
// response bodies in message/stack. Project fixed diagnostics, never serialize them.
type Context = { model?: string; provider?: string; attempt?: number; request_id?: string; conversation_id?: string; operation_id?: string };
function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

export function providerErrorSummary(error: unknown, context: Context = {}) {
  const value = record(error);
  const data = record(value.data$);
  const nested = record(value.error ?? data.error);
  const metadata = record(nested.metadata);
  const candidates = [value.statusCode, value.status, nested.code, value.code];
  const status = candidates.filter(value => typeof value === "number" || typeof value === "string").map(Number).find(code => Number.isInteger(code) && code >= 400 && code <= 599);
  const message = [value.message, metadata.raw, value.body, value.body$, data.body$, value.cause]
    .filter((text): text is string => typeof text === "string");
  const roleOrder = message.some(text => /Unexpected role ['\\"]*system['\\"]* after role/i.test(text));
  const databaseCode = typeof value.code === "string" && /^P\d{4}$/.test(value.code) ? value.code : undefined;
  const provider = typeof metadata.provider_name === "string" && ["DekaLLM", "DeepInfra", "Parasail"].includes(metadata.provider_name) ? metadata.provider_name : undefined;
  const projectedCode = value.name === "ProviderError" && typeof value.message === "string" &&
    ["provider_role_order", "provider_rate_limit", "provider_aborted", "provider_timeout", "database_error"].includes(value.message) ? value.message : undefined;
  const code = projectedCode ?? (databaseCode ? "database_error" : roleOrder ? "provider_role_order" : status === 429 ? "provider_rate_limit"
    : value.name === "AbortError" ? "provider_aborted"
    : value.name === "TimeoutError" ? "provider_timeout" : "provider_error");
  return {
    error_code: code,
    ...(status ? { http_status: status } : {}),
    ...(databaseCode ? { database_code: databaseCode } : {}),
    ...(provider ? { provider } : {}),
    ...context,
  };
}

export function safeProviderError(error: unknown) {
  const summary = providerErrorSummary(error);
  const safe = Object.assign(new Error(summary.error_code), {
    ...(summary.http_status ? { statusCode: summary.http_status } : {}),
  });
  safe.name = "ProviderError";
  // No cause, SDK properties, or original stack is copied.
  return safe;
}

export function isProviderError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current = error;
  for (let depth = 0; depth < 8 && current && !seen.has(current); depth++) {
    seen.add(current);
    const value = record(current);
    if ("data$" in value || "body$" in value || "rawResponse" in value || "request$" in value || "response$" in value ||
      (typeof value.name === "string" && /^(OpenRouter|.*ResponseError|SDK.*Error|.*SDKError|ProviderError)/.test(value.name)) ||
      (typeof value.cause === "string" && value.cause.startsWith("OpenRouter HTTP "))) return true;
    current = value.cause;
  }
  return false;
}

export function reportableError(error: unknown) {
  return isProviderError(error) ? safeProviderError(error) : error;
}
